import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { WaiterSummary, WaiterExcelLabels, exportWaiterExcel, formatSaleTime } from './waiterSalesReport';

interface WaiterSalesTabProps {
  summaries: WaiterSummary[];
  loading: boolean;
  /** Başlıkta gösterilen tarih aralığı, ör. "27.10.2026 – 27.10.2026" */
  rangeLabel: string;
  excelLabels: WaiterExcelLabels;
  fileSuffix: string;
}

const money = (n: number) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);
const qty = (n: number) => new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 3 }).format(n);
const pct = (n: number) => `%${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(n)}`;

const headCell = { fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', whiteSpace: 'nowrap' } as const;

const WaiterSalesTab: React.FC<WaiterSalesTabProps> = ({ summaries, loading, rangeLabel, excelLabels, fileSuffix }) => {
  const { t } = useTranslation('salesSystem');
  const k = (key: string, opts?: any) => t(`restaurantOrdersReport.waiters.${key}`, opts) as string;

  const [selected, setSelected] = useState<WaiterSummary | null>(null);
  const [view, setView] = useState<'product' | 'sale'>('product');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const totals = useMemo(
    () => ({
      sales: summaries.reduce((s, w) => s + w.saleCount, 0),
      quantity: summaries.reduce((s, w) => s + w.quantity, 0),
      total: summaries.reduce((s, w) => s + w.totalPrice, 0),
    }),
    [summaries],
  );

  const term = search.trim().toLocaleLowerCase('tr-TR');
  const visibleProducts = useMemo(
    () => (selected?.products ?? []).filter((p) => !term || p.productName.toLocaleLowerCase('tr-TR').includes(term)),
    [selected, term],
  );
  const visibleSales = useMemo(
    () =>
      (selected?.sales ?? []).filter(
        (s) =>
          !term ||
          s.items.some((i) => i.productName.toLocaleLowerCase('tr-TR').includes(term)) ||
          s.tableName.toLocaleLowerCase('tr-TR').includes(term) ||
          s.saleNumber.toLocaleLowerCase('tr-TR').includes(term),
      ),
    [selected, term],
  );

  const openWaiter = (w: WaiterSummary) => {
    setSelected(w);
    setView('product');
    setSearch('');
  };

  const downloadSelected = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const safe = selected.name.replace(/[^\p{L}\p{N}]+/gu, '_');
      await exportWaiterExcel([selected], excelLabels, `Garson_${safe}_${fileSuffix}.xlsx`, true);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (summaries.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
        <PersonIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">{k('empty')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: k('waiterCount'), value: String(summaries.length) },
          { label: k('addedQuantity'), value: qty(totals.quantity) },
          { label: k('addedTotal'), value: money(totals.total) },
        ].map((c) => (
          <Grid item xs={12} sm={4} key={c.label}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">{c.label}</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">{c.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {k('hint')}
      </Typography>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCell}>{k('colWaiter')}</TableCell>
              <TableCell sx={headCell} align="right">{k('colSales')}</TableCell>
              <TableCell sx={headCell} align="right">{k('colProductTypes')}</TableCell>
              <TableCell sx={headCell} align="right">{k('colQuantity')}</TableCell>
              <TableCell sx={headCell} align="right">{k('colTotal')}</TableCell>
              <TableCell sx={headCell} align="right">{k('colShare')}</TableCell>
              <TableCell sx={headCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {summaries.map((w) => (
              <TableRow key={w.key} hover sx={{ cursor: 'pointer' }} onClick={() => openWaiter(w)}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon fontSize="small" color={w.isUnknown ? 'disabled' : 'primary'} />
                    <Typography fontWeight={600} color={w.isUnknown ? 'text.secondary' : 'text.primary'}>
                      {w.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">{w.saleCount}</TableCell>
                <TableCell align="right">{w.productTypeCount}</TableCell>
                <TableCell align="right">{qty(w.quantity)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{money(w.totalPrice)}</TableCell>
                <TableCell align="right">{pct(w.share)}</TableCell>
                <TableCell align="right">
                  <Button size="small" endIcon={<ChevronRightIcon />} onClick={(e) => { e.stopPropagation(); openWaiter(w); }}>
                    {k('products')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: 'grey.50', '& td': { fontWeight: 700, borderTop: 2, borderColor: 'divider' } }}>
              <TableCell>{k('total')}</TableCell>
              <TableCell align="right">{totals.sales}</TableCell>
              <TableCell />
              <TableCell align="right">{qty(totals.quantity)}</TableCell>
              <TableCell align="right">{money(totals.total)}</TableCell>
              <TableCell align="right">%100</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        {k('salesNote')}
      </Typography>
      <Box sx={{ height: 80 }} />

      {/* Garson detayı */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700}>{k('modalTitle', { name: selected.name })}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {k('modalSubtitle', { range: rangeLabel, sales: selected.saleCount, quantity: qty(selected.quantity), total: money(selected.totalPrice) })}
                </Typography>
              </Box>
              <IconButton aria-label={k('close')} onClick={() => setSelected(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
                  <ToggleButton value="product">{k('byProduct')}</ToggleButton>
                  <ToggleButton value="sale">{k('bySale')}</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ flexGrow: 1 }} />
                <TextField
                  size="small"
                  placeholder={k('search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  sx={{ width: { xs: '100%', sm: 220 } }}
                />
              </Box>

              {view === 'product' ? (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>{k('colProduct')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{k('colSales')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{k('colQty')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{k('colAvgPrice')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{k('colAmount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleProducts.map((p) => (
                        <TableRow key={p.productName} hover>
                          <TableCell>{p.productName}</TableCell>
                          <TableCell align="right">{p.saleCount}</TableCell>
                          <TableCell align="right">{qty(p.quantity)}</TableCell>
                          <TableCell align="right">{money(p.quantity > 0 ? p.totalPrice / p.quantity : 0)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{money(p.totalPrice)}</TableCell>
                        </TableRow>
                      ))}
                      {visibleProducts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>{k('noMatch')}</TableCell>
                        </TableRow>
                      )}
                      {visibleProducts.length > 0 && (
                        <TableRow sx={{ bgcolor: 'grey.50', '& td': { fontWeight: 700 } }}>
                          <TableCell>{k('total')}</TableCell>
                          <TableCell />
                          <TableCell align="right">{qty(visibleProducts.reduce((s, p) => s + p.quantity, 0))}</TableCell>
                          <TableCell />
                          <TableCell align="right">{money(visibleProducts.reduce((s, p) => s + p.totalPrice, 0))}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>{k('colSaleNumber')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{k('colTable')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{k('colDate')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{k('colItems')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">{k('colAmount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleSales.map((s) => (
                        <TableRow key={s.saleId} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{s.saleNumber}</TableCell>
                          <TableCell>{s.tableName}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatSaleTime(s.completedAt)}</TableCell>
                          <TableCell>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {s.items.map((i, idx) => (
                                <Chip key={idx} size="small" variant="outlined" label={`${i.productName} ×${qty(i.quantity)}`} />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(s.totalPrice)}</TableCell>
                        </TableRow>
                      ))}
                      {visibleSales.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>{k('noMatch')}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />} disabled={exporting} onClick={downloadSelected}>
                {k('downloadExcel')}
              </Button>
              <Button variant="contained" onClick={() => setSelected(null)}>
                {k('close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default WaiterSalesTab;
